import React, { useMemo, useState, useEffect } from "react";
import { Skeleton } from '@mui/material';
import {Line,Tooltip,Legend,CartesianGrid,XAxis,YAxis,ResponsiveContainer,ComposedChart,ReferenceArea} from "recharts";

const SERIES_CONFIG = [
  { key: 'boilerMakeup', label: 'Boiler Makeup', color: '#47acebff', unit: 'lpm'},
  { key: 'preheatWater', label: 'Pre-Heated Water', color: '#3f596aff', unit: 'lpm'},
  { key: 'coolingWater', label: 'Cooling Water', color: '#5b8fa8ff', unit: 'lpm'},
  { key: 'steamProduction', label: 'Steam Production', color: '#8B4513', unit: 'lbs'},
  { key: 'boilerGas', label: 'Boiler Gas', color: '#FFA500', unit: 'm3'},
  { key: 'inletTemp', label: 'Soft Water Temp', color: '#e05c5c', unit: 'degF'},
  { key: 'outletTemp', label: 'Pre-Heated Water Temp', color: '#c0392b', unit: 'degF'},
  { key: 'boilerAmps', label: 'Boiler Amps', color: '#f39c12', unit: 'amps'},
  { key: 'boilerInput', label: 'Boiler Input', color: '#0872B6', unit: 'btu'},
  { key: 'steamLoad', label: 'Steam Load', color: '#00B1A8', unit: 'btu'},
  { key: 'energyRecovery', label: 'Energy Recovery', color: '#8884d8', unit: 'btu'},
  { key: 'totalEnergy', label: 'Total Energy to Process', color: '#FF8C42', unit: 'btu'},
  { key: 'heatLosses', label: 'Heat Losses', color: '#D7263D', unit: 'btu'},
  { key: 'boilerEfficiency', label: 'Boiler Efficiency', color: '#2A9D8F', unit: 'percentage'},
  { key: 'systemEfficiency', label: 'System Efficiency', color: '#E76F51',unit: 'percentage'},
];

const UNIT_CONFIG = {
  btu:{label: 'BTU'},
  lpm:{label: 'LPM'},
  lbs:{label: 'lbs'},
  degF:{label: '°F'},
  amps:{label: 'Amps'},
  m3:{label: 'm³'},
  percentage: {
    label: 'Efficiency (%)',
    domain: [0, 100],
    tickFormatter: (v) => `${v}%`,
  },
};

const SERIES_UNIT_MAP = Object.fromEntries(SERIES_CONFIG.map(s => [s.label, s.unit]));
export const KEY_UNIT_MAP = Object.fromEntries(SERIES_CONFIG.map(s => [s.key, s.unit]));

const formatTooltip = (value, name) => {
  const unit = SERIES_UNIT_MAP[name];
  const decimals = unit === 'btu' ? 0 : 2;
  const formatted = Math.round(value * 10 ** decimals) / 10 ** decimals;
  const formattedStr = formatted.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (unit === 'percentage') return [`${formattedStr}%`, name];
  const unitLabel = unit ? (UNIT_CONFIG[unit]?.label ?? unit) : '';
  return [`${formattedStr} ${unitLabel}`, name];
};

const formatYAxisTick = (tick) => {
  if (typeof tick !== 'number') return tick;
  if (Math.abs(tick) >= 1e6) return (tick / 1e6).toFixed(1) + 'M';
  if (Math.abs(tick) >= 1e5) return (tick / 1e3).toFixed(0) + 'k';
  return tick.toLocaleString();
};

const formatTimeOfDay = (dayFraction) => {
  const totalMinutes = Math.round(dayFraction * 24 * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const TICK_STEP_MINUTES = 5;
const TICK_STEP_FRACTION = TICK_STEP_MINUTES / (24 * 60);

const floorToTickStep = (dayFraction) => Math.floor(dayFraction / TICK_STEP_FRACTION) * TICK_STEP_FRACTION;

const getNiceTimeTicks = (min, max) => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return [];

  const ticks = [];
  const firstTick = floorToTickStep(min);
  for (let t = firstTick; t <= max + 1e-9; t += TICK_STEP_FRACTION) {
    ticks.push(t);
  }
  return ticks;
};

// Excel data uses day-fraction / serial-day numbers; Timestream returns real timestamps
// (ISO strings or epoch seconds/ms). Normalize the latter to epoch ms.
const toEpochMs = (t) => {
  if (typeof t === 'number') return t < 1e11 ? t * 1000 : t;
  const ms = new Date(t).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const isTimestampSeries = (time) => {
  const first = time.find((t) => t != null);
  if (first == null) return false;
  if (typeof first === 'number') return first > 1e9;
  return true;
};

const formatTimestamp = (ms, spanMs) => {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  const date = `${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (spanMs != null && spanMs <= 24 * 60 * 60 * 1000) return time;
  if (spanMs != null && spanMs > 3 * 24 * 60 * 60 * 1000) return date;
  return `${date} ${time}`;
};

const BoilerGraph = ({ metrics, checked, unitLimitTrigger, loading = false }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [refAreaLeft, setRefAreaLeft] = useState('');
  const [refAreaRight, setRefAreaRight] = useState('');
  const [zoomLeft, setZoomLeft] = useState(null);
  const [zoomRight, setZoomRight] = useState(null);

  const activeSeries = SERIES_CONFIG.filter(
    (s) => checked?.[s.key] && Array.isArray(metrics?.[s.key])
  );

  const activeUnitTypes = [...new Set(activeSeries.map((s) => s.unit))];
  const overLimit = activeUnitTypes.length > 2;
  const allowedUnits = new Set(activeUnitTypes.slice(0, 2));
  const displayedSeries = activeSeries.filter((s) => allowedUnits.has(s.unit));
  // First allowed unit → left axis, second → right axis
  const unitList = [...allowedUnits];

  useEffect(() => {
    if (overLimit) setShowPopup(true);
  }, [overLimit]);

  useEffect(() => {
    if (unitLimitTrigger) setShowPopup(true);
  }, [unitLimitTrigger]);

  // Reset zoom when data changes
  useEffect(() => {
    setZoomLeft(null);
    setZoomRight(null);
  }, [metrics]);

  const isTimestamp = useMemo(
    () => Array.isArray(metrics?.time) && isTimestampSeries(metrics.time),
    [metrics]
  );

  const chartData = useMemo(() => {
    const time = metrics?.time;
    if (!Array.isArray(time)) return [];
    const rows = time.map((t, i) => {
      const row = { time: isTimestamp ? toEpochMs(t) : t };
      displayedSeries.forEach((s) => {
        const value = metrics[s.key]?.[i];
        row[s.key] = s.unit === 'percentage' ? value * 100 : value;
      });
      return row;
    });
    return rows.filter((row) => Number.isFinite(row.time));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics, checked, isTimestamp]);

  const isZoomed = zoomLeft !== null && zoomRight !== null;

  const displayedData = useMemo(() => {
    if (!isZoomed) return chartData;
    return chartData.slice(zoomLeft, zoomRight + 1);
  }, [chartData, zoomLeft, zoomRight, isZoomed]);

  const timeSpan = useMemo(() => {
    if (!displayedData.length) return null;
    return displayedData[displayedData.length - 1].time - displayedData[0].time;
  }, [displayedData]);

  const xTicks = useMemo(() => {
    if (isTimestamp || !displayedData.length) return undefined;
    const times = displayedData.map((d) => d.time);
    return getNiceTimeTicks(Math.min(...times), Math.max(...times));
  }, [displayedData, isTimestamp]);

  const xTickFormatter = isTimestamp ? (ms) => formatTimestamp(ms, timeSpan) : formatTimeOfDay;
  const tooltipLabelFormatter = isTimestamp ? (ms) => formatTimestamp(ms) : formatTimeOfDay;

  const getYDomain = (unit) => {
    const keys = displayedSeries.filter((s) => s.unit === unit).map((s) => s.key);
    let min = Infinity, max = -Infinity;
    for (const row of displayedData) {
      for (const key of keys) {
        const v = parseFloat(row[key]);
        if (Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    if (!Number.isFinite(min)) return UNIT_CONFIG[unit]?.domain ?? ['auto', 'auto'];
    const range = Math.max(1, max - min);
    const pad = range * 0.05;
    if (unit === 'percentage') return [Math.max(0, Math.floor(min - pad)), Math.min(100, Math.ceil(max + pad))];
    return [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
  };

  const zoom = () => {
    if (!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
      setRefAreaLeft('');
      setRefAreaRight('');
      return;
    }

    let leftIdx = chartData.findIndex((d) => d.time === refAreaLeft);
    let rightIdx = chartData.findIndex((d) => d.time === refAreaRight);

    if (leftIdx > rightIdx) [leftIdx, rightIdx] = [rightIdx, leftIdx];

    setZoomLeft(leftIdx);
    setZoomRight(rightIdx);
    setRefAreaLeft('');
    setRefAreaRight('');
  };

  const zoomOut = () => {
    setZoomLeft(null);
    setZoomRight(null);
    setRefAreaLeft('');
    setRefAreaRight('');
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '400px' }}>
      {showPopup && (
        <div style={{
          position: 'absolute',
          top: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#ff4d4f',
          color: '#fff',
          padding: '8px 20px',
          borderRadius: '6px',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          whiteSpace: 'nowrap',
        }}>
          <span>Only 2 unit types on display at once</span>
          <button
            onClick={() => setShowPopup(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: 16,
              lineHeight: 1,
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {isZoomed && !loading && (
        <button
          onClick={zoomOut}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            background: 'rgba(217,29,29,0.85)',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          Zoom Out
        </button>
      )}

      <ResponsiveContainer>
        {loading ? (
          <Skeleton variant="rounded" animation="wave" width="100%" height="100%" />
        ) : (
        <ComposedChart
          data={displayedData}
          onMouseDown={(e) => e?.activeLabel && setRefAreaLeft(e.activeLabel)}
          onMouseMove={(e) => refAreaLeft && e?.activeLabel && setRefAreaRight(e.activeLabel)}
          onMouseUp={zoom}
        >
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            dataKey="time"
            type="number"
            scale={isTimestamp ? 'time' : 'auto'}
            domain={isTimestamp ? ['dataMin', 'dataMax'] : [(dataMin) => floorToTickStep(dataMin), 'dataMax']}
            ticks={xTicks}
            tickFormatter={xTickFormatter}
            minTickGap={20}
            tick={{ fontSize: '12px', fill: 'rgb(78,79,83)' }}
            label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
          />

          {unitList.map((unit, idx) => {
            const cfg = UNIT_CONFIG[unit];
            const orientation = idx === 0 ? 'left' : 'right';
            return (
              <YAxis
                key={unit}
                yAxisId={unit}
                orientation={orientation}
                domain={isZoomed ? getYDomain(unit) : cfg.domain}
                tickFormatter={cfg.tickFormatter || formatYAxisTick}
                tick={{ fontSize: '12px', fill: 'rgb(78,79,83)' }}
                label={{
                  value: cfg.label,
                  angle: orientation === 'left' ? -90 : 90,
                  position: orientation === 'left' ? 'insideLeft' : 'insideRight',
                  style: { fontSize: '12px', fill: 'rgb(78,79,83)' },
                }}
              />
            );
          })}

          <Tooltip formatter={formatTooltip} labelFormatter={tooltipLabelFormatter} />
          <Legend />

          {displayedSeries.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              yAxisId={s.unit}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
            />
          ))}

          {refAreaLeft && refAreaRight && unitList.length > 0 && (
            <ReferenceArea
              yAxisId={unitList[0]}
              x1={refAreaLeft}
              x2={refAreaRight}
              fill="rgba(136,132,216,0.15)"
              stroke="rgba(136,132,216,0.6)"
              strokeOpacity={0.8}
            />
          )}
        </ComposedChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default BoilerGraph;